import * as React from 'react';
import Tag from "@magoosh/lib/tag";
import {RatingData} from "types/rating";
import Tagger from "./tagger";
import {paths} from "config/path_helpers";
import {PrimaryButton} from "@magoosh/lib/button";
import debounce from 'lodash/debounce';
import fetch from "utilities/fetch";
import {OverlayTrigger, Popover} from "react-bootstrap";
import style from './style.module.scss';
import cx from 'classnames';

interface RatingFormProps {
  author?: {
    name: string;
    photo: string;
    bio: string;
  }
  lessonAuthorName: string;
  lessonAuthorThumbnail: string;
  rating?: RatingData;
}

// A component for ratings and feedback on a lesson.
// Info on the lesson's author also ended up here. When the lesson page is migrated to a
// 100% React page, we'll probably want to put it in its own component.
export default class RatingForm extends React.Component<RatingFormProps> {

  state = {
    text: this.props.rating && this.props.rating.text,
    rating: this.props.rating && this.props.rating.rating,
    alreadySubmitted: this.props.rating && this.props.rating.text && this.props.rating.text.length > 0, // Manually submitted, or typed and reloaded
    submitting: false,
    hasSubmitted: false // For showing the "thank you" message after submission
  };

  render() {
    return <div>
      <div className={style.authorAndRating}>
        {this.props.author && this.renderAuthorInfo()}
        {this.props.rating && this.renderBinaryFeedback()}
      </div>
      {this.props.rating && (
        <div className={style.feedbackDetails}>
          {this.state.hasSubmitted && this.renderThanks()}
          {this.renderFeedbackOptions()}
        </div>
      )}
    </div>
  }

  renderAuthorInfo = () => {
    return <div className={style.authorInfo}>
      <img src={this.props.author.photo} alt={this.props.author.name}
           className={style.authorPhoto}/>
      <div className={style.authorCredits}>
        Lesson by <OverlayTrigger overlay={this.renderAuthorBio()}
                                  placement="bottom"
                                  rootClose
                                  trigger={["hover", "focus"]}
      >
        <strong id="lesson-author-name"
                className={style.popoverItem}>{this.props.author.name}</strong>
      </OverlayTrigger>
        <div><small className="text-gray-light">Magoosh Expert</small></div>
      </div>
    </div>
  };


  renderAuthorBio = () => {
    return <Popover id="lesson-author-bio" title={`About ${this.props.author.name}`}>
      <div>
        {this.props.author.bio}
      </div>
    </Popover>
  };

  renderBinaryFeedback = () => {
    return <div className={style.binaryFeedback}>
      <div className="u-margin-B-xxs"><strong>Lesson Feedback:</strong> Did you like this lesson?</div>
      <div>
        <Tag displayName={"Yes, I Liked It"} onClickCallback={this.thumbsUp}
             isSelected={this.state.rating > 0} canBeSelected={true}/>
        <Tag displayName={"No, I Didn't Like It"} onClickCallback={this.thumbsDown}
             isSelected={this.state.rating < 0} canBeSelected={true} negative={true}/>
      </div>
    </div>
  };

  renderFeedbackOptions = () => {
    if (this.state.hasSubmitted || this.state.alreadySubmitted || !this.state.rating) {
      return null;
    }

    const rating = this.state.rating;
    return <div>
      <div className="u-margin-T-m u-border-V-s u-padding-V-m">
        {rating < 0 &&
        <div className={"u-margin-B-s"}>
            <div className="u-margin-B-xs">
                <strong>What didn't you like about this
                    lesson?</strong> <small>(choose all that apply)</small>
            </div>
            <Tagger options={this.props.rating.reasons}
                    paramNamespace={'rating'}
                    rateableType={'Video::File'}
                    url={paths.api.rating(this.props.rating.rateableId)}/>
        </div>
        }

        <label htmlFor={'rating-text'}>
          <strong>
            {rating < 0 ? "How can we improve?" : "What did you like about this lesson?"}
          </strong>
        </label>
        <textarea name={'rating.text'} defaultValue={this.state.text} id='rating-text' rows={5}
                  onChange={this.onChangeText}/>
        <div className={'u-margin-T-s'}>
          <PrimaryButton submitting={this.state.submitting}
                         onClick={this.onClickSubmitRating}>
            Submit Feedback
          </PrimaryButton>
          <a className={'pull-right'} href={"#"} rel={"zenbox"}>I have a question or need help with this lesson.</a>
        </div>
      </div>

    </div>
  };

  renderThanks = () => {
    return <div
      className={cx('u-margin-T-s', 'alert', this.state.rating > 0 ? 'alert-success' : 'alert-default' ) }>
      <strong>Thanks for letting us know!</strong>
      <br/>
      We may not be able to respond to all of your feedback, but we promise to read and consider all
      of it :)
    </div>
  };

  // Feedback text is submitted automatically whenever the user stops typing,
  // but the "thank you" message only appears when they manually submit it.
  onChangeText = (event) => {
    this.setState({text: event.target.value, saved: false}, this.backgroundSubmit);
  };

  backgroundSubmit = debounce(async () => {
      this.onSubmitRating()
    }, 2000, {trailing: true}
  );

  onClickSubmitRating = () => {
    this.setState({submitting: true}, () => {
      this.onSubmitRating(true);
    });
  };

  onSubmitRating = (manualSubmit = false) => {
    fetch(paths.api.rating(this.props.rating.rateableId), {
      method: 'PUT',
      body: JSON.stringify({
        rateableType: 'Video::File',
        rating: {
          rating: this.state.rating,
          text: this.state.text
        }
      })
    }).then(response => {
      this.setState({
        hasSubmitted: this.state.hasSubmitted || manualSubmit,
        submitting: false
      })
    })
  };

  thumbsUp = () => {
    this.setState({rating: 1}, this.onSubmitRating)
  };

  thumbsDown = () => {
    this.setState({rating: -1}, this.onSubmitRating)
  };

}
